import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

// Sim duration; a profile shorter than this holds its last value.
const TOTAL_TIME_S = 600;
const MIN_K = 223.15;   // -50 °C
const MAX_K = 1773.15;  // 1500 °C
const MAX_ROWS = 2000;

const toSeconds = (t, unit) => (unit === 'min' ? t * 60 : t);
const toKelvin = (T, unit) =>
  unit === 'C' ? T + 273.15 : unit === 'F' ? ((T - 32) * 5) / 9 + 273.15 : T;

// CSV -> array of [time, temp] in the file's own units (header row auto-skipped).
function parseCSV(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) throw new Error('The file is empty.');
  const rows = [];
  lines.forEach((line, i) => {
    const cells = line.split(/[,;\t]/).map((c) => c.trim());
    const a = Number(cells[0]);
    const b = Number(cells[1]);
    if (i === 0 && (!Number.isFinite(a) || !Number.isFinite(b))) return; // header
    if (cells.length < 2) throw new Error(`Row ${i + 1}: expected 2 columns (time, temperature).`);
    if (!Number.isFinite(a)) throw new Error(`Row ${i + 1}: time "${cells[0]}" is not a number.`);
    if (!Number.isFinite(b)) throw new Error(`Row ${i + 1}: temperature "${cells[1]}" is not a number.`);
    rows.push([a, b]);
  });
  return rows;
}

// JSON -> array of [time, temp]. Accepts pairs, {time,temperature} arrays, or objects.
function parseJSONProfile(text) {
  let data;
  try { data = JSON.parse(text); } catch (e) { throw new Error('Invalid JSON: ' + e.message); }
  if (Array.isArray(data)) {
    if (data.length && Array.isArray(data[0])) {
      return data.map((p, i) => {
        if (p.length < 2) throw new Error(`Item ${i + 1}: expected [time, temperature].`);
        return [Number(p[0]), Number(p[1])];
      });
    }
    if (data.length && typeof data[0] === 'object' && data[0] !== null) {
      return data.map((o) => [Number(o.time ?? o.t), Number(o.temp ?? o.temperature ?? o.T)]);
    }
    throw new Error('JSON array must contain [time,temp] pairs or {time,temp} objects.');
  }
  if (data && typeof data === 'object' && Array.isArray(data.time)) {
    const temps = data.temperature ?? data.temp;
    if (!Array.isArray(temps) || temps.length !== data.time.length)
      throw new Error('JSON must have equal-length "time" and "temperature" arrays.');
    return data.time.map((t, i) => [Number(t), Number(temps[i])]);
  }
  throw new Error('Unrecognized JSON shape. See the instructions.');
}

// Raw rows (chosen units) -> validated [[t_s, T_K]] + warnings, or throws.
function buildProfile(rawRows, timeUnit, tempUnit) {
  if (!rawRows || rawRows.length < 2) throw new Error('Need at least 2 points.');
  if (rawRows.length > MAX_ROWS) throw new Error(`Too many points (max ${MAX_ROWS}).`);
  const pts = rawRows.map(([t, T], i) => {
    if (!Number.isFinite(t) || !Number.isFinite(T))
      throw new Error(`Row ${i + 1}: time and temperature must both be numbers.`);
    return [toSeconds(t, timeUnit), toKelvin(T, tempUnit)];
  });
  if (Math.abs(pts[0][0]) > 1e-6) throw new Error('The first time value must be 0.');
  for (let i = 1; i < pts.length; i++) {
    if (pts[i][0] <= pts[i - 1][0])
      throw new Error(`Time must strictly increase (row ${i + 1} is not after row ${i}).`);
  }
  for (let i = 0; i < pts.length; i++) {
    if (pts[i][1] < MIN_K || pts[i][1] > MAX_K)
      throw new Error(`Row ${i + 1}: temperature is out of range (allowed ≈ −50…1500 °C).`);
  }
  const warnings = [];
  const lastT = pts[pts.length - 1][0];
  if (lastT < TOTAL_TIME_S)
    warnings.push(`Profile ends at ${(lastT / 60).toFixed(1)} min; the last value is held to 10 min.`);
  return {
    points: pts.map(([t, T]) => [Number(t.toFixed(2)), Number(T.toFixed(2))]),
    warnings,
  };
}

const CustomProfileModal = ({ initialStep = 'intro', onClose, onUse, onSeeInstructions }) => {
  const [step, setStep] = useState(initialStep);
  const [timeUnit, setTimeUnit] = useState('min');
  const [tempUnit, setTempUnit] = useState('C');
  const [fileName, setFileName] = useState('');
  const [rawRows, setRawRows] = useState(null);
  const [readError, setReadError] = useState(null);

  // Re-validate whenever the rows or units change (units affect the conversion).
  const result = useMemo(() => {
    if (!rawRows) return null;
    try { return { ok: true, ...buildProfile(rawRows, timeUnit, tempUnit) }; }
    catch (e) { return { ok: false, message: e.message }; }
  }, [rawRows, timeUnit, tempUnit]);

  const handleFile = (file) => {
    setReadError(null);
    setRawRows(null);
    setFileName('');
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result);
        const rows = file.name.toLowerCase().endsWith('.json')
          ? parseJSONProfile(text)
          : parseCSV(text);
        setRawRows(rows);
      } catch (e) {
        setRawRows(null);
        setReadError(e.message);
      }
    };
    reader.onerror = () => setReadError('Could not read the file.');
    reader.readAsText(file);
  };

  const handleDownloadTemplate = () => {
    const tHdr = timeUnit === 'min' ? 'time_min' : 'time_s';
    const THdr = tempUnit === 'C' ? 'temp_C' : tempUnit === 'F' ? 'temp_F' : 'temp_K';
    const sample = timeUnit === 'min'
      ? [[0, 20], [2, 40], [3.9, 600], [5, 470], [10, 420]]
      : [[0, 20], [120, 40], [234, 600], [300, 470], [600, 420]];
    const conv = sample.map(([t, c]) => {
      const T = tempUnit === 'C' ? c : tempUnit === 'F' ? (c * 9) / 5 + 32 : c + 273.15;
      return `${t},${Math.round(T * 100) / 100}`;
    });
    const blob = new Blob([`${tHdr},${THdr}\n${conv.join('\n')}\n`], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'custom_fire_profile_template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const previewRows = useMemo(() => {
    if (!result?.ok) return [];
    return result.points.map(([t, T]) => ({
      time: Number((t / 60).toFixed(2)),
      temp: Math.round(T - 273.15),
    }));
  }, [result]);

  const handleUse = () => {
    if (result?.ok) onUse({ points: result.points, name: fileName || 'custom profile' });
  };

  return (
    <div className="license-modal-backdrop" role="dialog" aria-modal="true">
      <div className="license-modal custom-profile-modal">
        {step === 'intro' ? (
          <>
            <div className="license-modal-header"><h2>Custom fire profile</h2></div>
            <div className="license-modal-body cp-body">
              <p>You can upload your own inlet-gas temperature curve as a CSV or JSON file.</p>
              <p>
                <strong>Please review the required format before uploading.</strong>{' '}
                <button type="button" className="cp-link" onClick={onSeeInstructions}>
                  See instructions in the User Guide
                </button>.
              </p>
              <p>Click OK when you're ready to upload.</p>
            </div>
            <div className="license-modal-footer cp-footer">
              <button type="button" className="cp-btn-secondary" onClick={onClose}>Cancel</button>
              <button type="button" className="license-modal-agree-btn" onClick={() => setStep('upload')}>OK</button>
            </div>
          </>
        ) : (
          <>
            <div className="license-modal-header"><h2>Upload custom fire profile</h2></div>
            <div className="license-modal-body cp-body">
              <div className="cp-row">
                <label>Time unit</label>
                <select value={timeUnit} onChange={(e) => setTimeUnit(e.target.value)}>
                  <option value="min">minutes</option>
                  <option value="s">seconds</option>
                </select>
                <label>Temperature unit</label>
                <select value={tempUnit} onChange={(e) => setTempUnit(e.target.value)}>
                  <option value="C">°C</option>
                  <option value="F">°F</option>
                  <option value="K">K</option>
                </select>
              </div>
              <div className="cp-row">
                <input type="file" accept=".csv,.json" onChange={(e) => handleFile(e.target.files?.[0])} />
                <button type="button" className="cp-btn-secondary" onClick={handleDownloadTemplate}>
                  Download template
                </button>
              </div>
              <p className="cp-hint">
                Need the format?{' '}
                <button type="button" className="cp-link" onClick={onSeeInstructions}>See instructions</button>.
              </p>

              {readError && <div className="cp-error">{readError}</div>}
              {result && !result.ok && <div className="cp-error">{result.message}</div>}
              {result?.ok && result.warnings.map((w, i) => <div key={i} className="cp-warn">{w}</div>)}

              {result?.ok && previewRows.length > 0 && (
                <div className="cp-preview">
                  <div className="cp-preview-title">Preview: {result.points.length} points</div>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={previewRows} margin={{ top: 6, right: 14, left: 6, bottom: 18 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" />
                      <XAxis
                        dataKey="time" type="number" tick={{ fill: 'var(--border-strong)', fontSize: 11 }}
                        label={{ value: 'Time (min)', position: 'insideBottom', offset: -4, fill: 'var(--border-strong)', fontSize: 11 }}
                      />
                      <YAxis
                        tick={{ fill: 'var(--border-strong)', fontSize: 11 }}
                        label={{ value: '°C', angle: -90, position: 'insideLeft', fill: 'var(--border-strong)', fontSize: 11 }}
                      />
                      <Tooltip
                        contentStyle={{ background: 'var(--text-on-dark)', border: '1px solid var(--text-on-dark-muted)', color: 'var(--text-on-light-secondary)' }}
                        labelFormatter={(v) => `${v} min`}
                        formatter={(v) => [`${v} °C`, 'Temp']}
                      />
                      <Line type="monotone" dataKey="temp" stroke="#0072B2" dot={false} strokeWidth={2} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div className="license-modal-footer cp-footer">
              <button type="button" className="cp-btn-secondary" onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="license-modal-agree-btn"
                onClick={handleUse}
                disabled={!result?.ok}
              >
                Use this profile
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomProfileModal;
